import { useEffect } from 'react';
import {
  CalendarNavigation,
  ScheduleListView,
  selectDisplayMode,
  setCurrentDate,
  setEditingAppointmentId,
  setMockAppointments,
  setTutorialMode,
  setViewMode,
} from '@features/appointments';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { ExperimentalCalendarSidebar } from './components/CalendarSidebar/CalendarSidebar';
import CalendarWidget from './components/CalendarWidget/CalendarWidget';
import { EligibilityDrawer } from './components/EligibilityDrawer/EligibilityDrawer';
import { AppointmentOverridesProvider } from './context/AppointmentOverridesContext';
import { EligibilityDrawerProvider } from './context/EligibilityDrawerContext';
import { ExperimentalCalendarFiltersProvider } from './context/ExperimentalCalendarFilters';
import { FIXTURES_DEMO_NOW, getFixtureAppointments } from './fixtures';

export const ScheduleEligibilityExperiment = () => {
  const dispatch = useAppDispatch();
  const displayMode = useAppSelector(selectDisplayMode);

  // Statsig gate boilerplate. Route is currently dev-only via devRoutes.tsx,
  // so the value is informational (and emits an exposure event for telemetry).
  // When ramping to prod, move the route to mainRoutes.tsx and gate access on
  // this value (with a Navigate fallback when false).
  const isGateEnabled = useFeatureGate(FeatureGates.ELIGIBILITY_CALENDAR);
  void isGateEnabled;

  // Feed fixture data through the existing useAppointments tutorial-mode path.
  // On unmount, restore default state so /schedule keeps working.
  useEffect(() => {
    const fixtures = getFixtureAppointments();
    dispatch(setTutorialMode(true));
    dispatch(setMockAppointments(fixtures));
    dispatch(setCurrentDate(FIXTURES_DEMO_NOW.toISOString()));
    dispatch(setViewMode('week'));

    return () => {
      dispatch(setTutorialMode(false));
      dispatch(setMockAppointments([]));
      dispatch(setEditingAppointmentId(null));
    };
  }, [dispatch]);

  return (
    <AppointmentOverridesProvider>
      <ExperimentalCalendarFiltersProvider>
        <EligibilityDrawerProvider>
        <Box
          className="eligibility-experiment"
          sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: ELIG_TOKENS.bg,
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <CalendarNavigation />
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row' }}>
            <Box
              sx={{
                width: 256,
                flexShrink: 0,
                overflow: 'auto',
                borderRight: `1px solid ${ELIG_TOKENS.line}`,
              }}
            >
              <ExperimentalCalendarSidebar />
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                mx: 2,
                mb: 2,
                borderRadius: 3,
                bgcolor: ELIG_TOKENS.card,
                overflow: 'auto',
              }}
            >
              {displayMode === 'list' ? (
                <ScheduleListView onAppointmentDelete={() => Promise.resolve()} />
              ) : (
                <CalendarWidget />
              )}
            </Box>
          </Box>
        </Box>

          <EligibilityDrawer />
        </EligibilityDrawerProvider>
      </ExperimentalCalendarFiltersProvider>
    </AppointmentOverridesProvider>
  );
};
